import React, { useCallback } from 'react';
import Big from 'big.js';
import IPurchaseForm, { TPurchaseState, IUsePurchaseForm } from './IPurchaseForm';
import { purchaseAmountValidation } from '@funds/features/helpers/validation';
import { IWalletDetails } from '@market/interfaces/IWallet';
import { IGetStringKey } from '@appInterfaces/general.types';
import { getTotalToPay } from './getPurchaseFees';
import { getExchangeRateUpdateTime } from '../ExchangeForm/useExchangeForm';

export const usePurchaseForm = (props: IPurchaseForm): IUsePurchaseForm => {
  let _activeWallet = undefined;
  let _errors = {};
  const initialValue: TPurchaseState = {
    step: 1,
    amount: undefined,
    currencyToPay: props.availableToPayWallets[0].currency,
    errors: {},
    nextStepEnable: false,
    exchangeRateChangeTime: getExchangeRateUpdateTime(),
    message: '',
    sentExchangeRate: '',
  };

  const [state, dispatch] = React.useReducer(useCallback((state: TPurchaseState, action) => {
    switch (action.type) {
      case 'edit':
        return {
          ...state,
          ...action.payload,
        };

      case 'setMessage':
        return {
          ...state,
          message: action.payload as string,
        };

      case 'moveToStep':
        return {
          ...state,
          step: action.payload,
        };

      case 'handleTimerEnd':
        return {
          ...state,
          exchangeRateChangeTime: getExchangeRateUpdateTime(),
        };

      case 'handleSubmit':
        return  {
          ...state,
          sentExchangeRate: action.payload as string,
          exchangeRateChangeTime: undefined,
        };

      case 'handleBackButton':
        return  {
          ...state,
          sentExchangeRate: '',
          exchangeRateChangeTime: getExchangeRateUpdateTime(),
          message: '',
        };

      case 'updateCurrencyToPay':
        _activeWallet = props.availableToPayWallets.find(wallet => wallet.currency === action.payload)!;
        _errors = validatePurchaseForm(action.payload, _activeWallet, props, state.amount);
        return {
          ...state,
          currencyToPay: action.payload,
          errors: _errors,
          nextStepEnable: stepValid(state.step, _errors, Number(state.amount)),
        };

      case 'purchaseAll':
        _activeWallet = props.availableToPayWallets.find(wallet => wallet.currency === state.currencyToPay)!;
        const purchaseFee = Big(Number(props.purchaseFee) / 100).plus(1);
        const exchangeRate = Big(props.purchaseExchangeRates!['bft_' + _activeWallet.currency]);
        const exchangeReducedByFee = exchangeRate.div(purchaseFee);
        const maxBftAmount = Big(_activeWallet.balance).times(exchangeReducedByFee).round(0, 0);
        const maxTokens = Big(maxBftAmount).gt(props.purchaseWallet.available_purchase_cap!) ?
          Number(props.purchaseWallet.available_purchase_cap) : Number(maxBftAmount);
        _errors = validatePurchaseForm(state.currencyToPay, _activeWallet, props, maxTokens);
        return {
          ...state,
          amount: maxTokens,
          errors: _errors,
          nextStepEnable: stepValid(state.step, _errors, maxTokens),
        };

      case 'updateAmount':
        const amount = parseInt(action.payload, 10) || '';
        _activeWallet = props.availableToPayWallets.find(wallet => wallet.currency === state.currencyToPay)!;
        _errors = validatePurchaseForm(state.currencyToPay, _activeWallet, props, Number(amount));
        return {
          ...state,
          amount,
          errors: _errors,
          nextStepEnable: stepValid(state.step, _errors, Number(amount)),
        };

      default:
        return state;
    }
  }, [props]), initialValue);

  return {
    state,
    dispatch,
  };
};

const stepValid = (step: number, errors: IGetStringKey, amount: number): boolean => {
  switch (step) {
    case 1:
      return errors.amount == null && !!amount;
    case 2:
      return true;
    default:
      return false;
  }
};

const validatePurchaseForm = (
  currencyToPay: string, wallet: IWalletDetails, props: IPurchaseForm, amount?: number
): {} => {
  return {
    amount: purchaseAmountValidation(
      wallet,
      props.purchaseExchangeRates,
      getTotalToPay(amount || 0, currencyToPay, props.purchaseExchangeRates, props.purchaseFee),
      props.purchaseWallet!,
      props.minPurchaseUsdValue,
      amount
    ),
  };
};
